import { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolder } from "@fortawesome/free-solid-svg-icons";
import DataTable from "../../core/DataTable.jsx";
import { Link } from "react-router-dom";

import Loading from "../../layout/Loading.jsx";
import Button from "../../core/Button.jsx";
import Badge from "../../core/Badge.jsx";
import SearchInput from "../../input/SearchInput.jsx";
import useTriggeredAPICall from "../../../hooks/fetch-hooks/useTriggeredAPICall.js";
import usePagination from "../../../hooks/usePagination.js";
import useSearchTerm from "../../../hooks/useSearchTerm.js";
import useDebounce from "../../../hooks/useDebounce.js";
import useDelayedLoading from "../../../hooks/useDelayedLoading.js";
import SecurityWrapper from "../../auth/SecurityWrapper.jsx";

const ProjectListPage = () => {
  const [searchTerm, setSearchTerm] = useSearchTerm();
  const [filterProjects, setFilterProjects] = useState("active");
  const history = useHistory();

  const [sortParams, setSortParams] = useState(() => {
    const queryParams = new URLSearchParams(history.location.search);

    return {
      sortBy: queryParams.get("sort") || "",
      sortDir: queryParams.get("sort_dir") || "asc",
    };
  });
  const searchDebounce = useDebounce(searchTerm);

  const { fetchData, data, isLoading } = useTriggeredAPICall();

  const endpoint = useMemo(() => {
    const params = new URLSearchParams({ status: filterProjects });
    params.set("sort", sortParams.sortBy);
    params.set("sort_dir", sortParams.sortDir);

    if (searchDebounce) {
      params.set("q", searchDebounce);
    }
    return `/projects/status?${params.toString()}`;
  }, [filterProjects, sortParams, searchDebounce]);

  const { currentData, recordsData, listPage } = usePagination(
    data,
    endpoint,
    fetchData
  );

  const showLoading = useDelayedLoading(isLoading);

  const columns = [
    {
      id: "project_color",
      name: "",
      width: "8px",
      minWidth: "8px",
      maxWidth: "8px",
      sortable: false,
      cell: (row) => (
        <div
          style={{
            backgroundColor: `${row.color}99`,
            width: "100%",
            height: "38px",
            marginTop: "5px",
            marginBottom: "5px",
          }}
        />
      ),
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      width: "35%",
      minWidth: "160px",
      cell: (row) => (
        <Link
          className="table-link truncate"
          to={{ pathname: `/project/${row.project_id}` }}
        >
          {row.name}
        </Link>
      ),
    },
    {
      name: "Product Owner",
      selector: (row) =>
        `${row.product_owner?.first_name ?? ""} ${
          row.product_owner?.last_name ?? ""
        }`.trim(),
      sortable: true,
      width: "25%",
      minWidth: "120px",
    },
    {
      id: "status",
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      width: "12%",
      minWidth: "75px",
      cell: (row) => <Badge status={row.status} type="status" />,
    },
    {
      id: "project_health",
      name: "Health",
      selector: (row) => row.project_health,
      sortable: true,
      width: "12%",
      minWidth: "75px",
      cell: (row) => <Badge status={row.project_health} type="health" />,
    },
    {
      id: "due_date",
      name: "Due Date",
      selector: (row) => row.due_date,
      sortable: true,
      width: "16%",
      minWidth: "80px",
      cell: (row) => (
        <span>
          {row.due_date ? new Date(row.due_date).toLocaleDateString() : "-"}
        </span>
      ),
    },
  ];

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);

    if (sortParams.sortBy) {
      params.set("sort", sortParams.sortBy);
      params.set("sort_dir", sortParams.sortDir);
    } else {
      params.delete("sort");
      params.delete("sort_dir");
    }

    history.replace({
      pathname: history.location.pathname,
      search: params.toString(),
    });
  }, [sortParams, history]);

  useEffect(() => {
    listPage(1);
  }, [endpoint, searchDebounce]);

  if (showLoading && !currentData) {
    return <Loading />;
  }

  if (!currentData) {
    return null;
  }

  return (
    <div className="flex-align-start width-full">
      <h1>
        <FontAwesomeIcon icon={faFolder} className="projects-icon" /> Projects
      </h1>
      <div className="flex-space-between">
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="width-200px"
          />
          <label>
            <input
              type="checkbox"
              checked={filterProjects === "all"}
              onChange={() => {
                setFilterProjects("all");
              }}
            />
            <span className="ml-2">All</span>
          </label>

          <label>
            <input
              type="checkbox"
              checked={filterProjects === "active"}
              onChange={() => {
                setFilterProjects("active");
              }}
            />
            <span className="ml-2">Show Active</span>
          </label>

          <label>
            <input
              type="checkbox"
              checked={filterProjects === "inactive"}
              onChange={() => {
                setFilterProjects("inactive");
              }}
            />
            <span className="ml-2">Show Inactive</span>
          </label>
        </div>
        <SecurityWrapper requiredRoles={["admin"]}>
          <Button to="/project-form">+ New Project</Button>
        </SecurityWrapper>
      </div>

      <DataTable
        columns={columns}
        data={currentData}
        loading={showLoading}
        onRowClick={(row) => history.push(`/project/${row.project_id}`)}
        pointerOnHover
        highlightOnHover
        recordsData={recordsData}
        onPageChange={listPage}
        baseUrl={endpoint}
        setSortParams={setSortParams}
        sortParams={sortParams}
        defaultSortFieldId={sortParams?.sortBy}
        defaultSortAsc={sortParams?.sortDir !== "desc"}
        isSortedManually={true}
      />
    </div>
  );
};

export default ProjectListPage;
